import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { experienceContextQueryKey } from "@src/packages/experience/hooks/useExperienceContext";
import type {
  AcceptedTreatmentPlan,
  AcceptedTreatmentPlanPayload,
} from "../treatmentPlanTypes";

export type {
  AcceptedTreatmentPlan,
  AcceptedTreatmentPlanPayload,
} from "../treatmentPlanTypes";

export function treatmentPlanQueryKey(patientId: string | null) {
  return ["accepted-treatment-plan", patientId] as const;
}

function createIdempotencyKey() {
  const runtimeCrypto = globalThis.crypto;
  if (runtimeCrypto?.randomUUID) return runtimeCrypto.randomUUID();

  const randomHex = (length: number) =>
    Array.from({ length }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join("");
  const variant = (8 + Math.floor(Math.random() * 4)).toString(16);
  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-${variant}${randomHex(3)}-${randomHex(12)}`;
}

export function useTreatmentPlan() {
  const { client, patientId } = useApiClient();
  const queryKey = treatmentPlanQueryKey(patientId);

  return useQuery<AcceptedTreatmentPlan | null>({
    queryKey,
    queryFn: async () => {
      if (!patientId) return null;
      const { data, error, response } = await client.GET(
        "/patients/{patientId}/treatment-plan",
        { params: { path: { patientId } } },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to load the accepted treatment plan",
          "useTreatmentPlan",
          response?.status,
        );
      }
      return data?.data ?? null;
    },
    enabled: !!patientId,
  });
}

export function useAcceptTreatmentPlan() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = treatmentPlanQueryKey(patientId);

  return useMutation({
    mutationFn: async (payload: AcceptedTreatmentPlanPayload) => {
      if (!patientId) throw new Error("No patient context");
      const { data, error, response } = await client.POST(
        "/patients/{patientId}/treatment-plan",
        {
          params: {
            path: { patientId },
            header: { "Idempotency-Key": createIdempotencyKey() },
          },
          body: payload,
        },
      );
      if (error) {
        throwApiError(
          error,
          "Failed to save the accepted treatment plan",
          "useAcceptTreatmentPlan",
          response?.status,
        );
      }
      if (!data?.data) {
        throw new Error("Accepted treatment plan response was empty");
      }
      return data.data;
    },
    onSuccess: (plan) => {
      queryClient.setQueryData<AcceptedTreatmentPlan | null>(queryKey, plan);
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: experienceContextQueryKey(patientId),
      });
    },
  });
}
